import React, { FocusEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  _colorText,
  _size,
  _sizeText,
  _style,
  _typeInput,
} from "../../core/core";
import Calendar from "../Calandar/Calendar";
import { ModalBase } from "../Modal/ModalBase";
import { Text } from "../texts/text";
import "./InputDatePicker.css";

interface InputDatePickerProps {
  size?: _size;
  type?: _typeInput;
  labelOutside?: string;
  label?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  description?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  value: number;
  setValue: React.Dispatch<React.SetStateAction<number>>;
  focusStart?: boolean;
  validation?: () => string;
}

export const InputDatePicker: React.FC<InputDatePickerProps> = ({
  size = _size.M,
  type = _typeInput.date,
  labelOutside,
  label,
  placeholder,
  min,
  max,
  description,
  onKeyDown,
  value,
  setValue,
  focusStart = false,
  validation,
}) => {
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [focus, setFocus] = useState<boolean>(focusStart);
  const [messageError] = useState<string>("");
  const calendarRef = useRef<HTMLDivElement>(null);
  const isMobile = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia("(max-width: 767px)").matches;
    }
    return false;
  }, []);
  const modalRef = useRef<HTMLDivElement | null>(null);

  const variantsClassName = (): string => {
    return validation && messageError !== ""
      ? `input-field input-field-${size} ${type} error`
      : `input-field input-field-${size} ${type} focus-${focus}`;
  };

  const handleDateChange = (date: Date): void => {
    setValue(+date);
  };

  const handleTimeChange = (time: string): void => {
    if (value) {
      setValue(value);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
    setFocus(false);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      console.log("handleClickOutside");
      if (!showCalendar) return;
      const target = event.target as Node;
      // Убедимся, что элемент modal существует и проверяем клик внутри/вне его
      if (modalRef.current && !modalRef.current.contains(target)) {
        setShowCalendar(false);
      }
    }

    function handleEscapeKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setShowCalendar(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [showCalendar]);
  const handleFocus = (): void => {
    setShowCalendar(true);
    setFocus(true);
  };
  const handleChange = (e: React.ChangeEvent): void => {};

  const getDatetimeView = (): string => {
    const datetime = new Date(value);
    const time = datetime.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${datetime.toLocaleDateString()} ${time}`;
  };

  return (
    <div className="field input-date-picker" ref={calendarRef}>
      {labelOutside && (
        <Text size={_sizeText.xs} color={_colorText.neutral}>
          {labelOutside}
        </Text>
      )}
      <div className={variantsClassName()}>
        {description && (
          <Text size={_sizeText.xs} color={_colorText.neutral}>
            {description}
          </Text>
        )}
        <div className="field-content">
          <input
            type="text"
            value={getDatetimeView()}
            placeholder={placeholder || label}
            onFocus={handleFocus}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            autoFocus={showCalendar}
          />
        </div>
        {showCalendar && (
          <div ref={modalRef}>
            {isMobile ? (
              <ModalBase
                onClose={() => setShowCalendar(false)}
                active={showCalendar}
                button_style={_style.outline}
                setActive={setShowCalendar}
              >
                <Calendar
                  minDate={min ? new Date(min) : undefined}
                  maxDate={max ? new Date(max) : undefined}
                  currentDate={new Date(value)}
                  onDateChange={handleDateChange}
                  onTimeChange={handleTimeChange}
                />
              </ModalBase>
            ) : (
              <div className="calendar-popup">
                <Calendar
                  minDate={min ? new Date(min) : undefined}
                  maxDate={max ? new Date(max) : undefined}
                  currentDate={new Date(value)}
                  onDateChange={handleDateChange}
                  onTimeChange={handleTimeChange}
                  is_shadow
                />
              </div>
            )}
          </div>
        )}
      </div>
      {description && (
        <Text size={_sizeText.xs} color={_colorText.neutral}>
          {description}
        </Text>
      )}
      {messageError && (
        <Text size={_sizeText.xs} color={_colorText.negative}>
          {messageError}
        </Text>
      )}
    </div>
  );
};
